/**
 * Mean of array
 */
const mean = (data: number[]): number => {
  if (data.length === 0) {
    return 0;
  }
  return data.reduce((sum, val) => sum + val, 0) / data.length;
};

/**
 * Linear spacing
 */
const linspace = (start: number, end: number, num: number): number[] => {
  if (num <= 0) {
    return [];
  }
  if (num === 1) {
    return [start];
  }
  const step = (end - start) / (num - 1);
  return Array.from({ length: num }, (_, i) => start + i * step);
};

/**
 * Check if number is 5-smooth (only prime factors 2, 3, 5)
 * This ensures the FFT is efficient (O(N log N) with small constants)
 */
const isSmooth = (n: number): boolean => {
  if (n <= 0) {
    {
      return false;
    }
  }
  while (n % 2 === 0) {
    n /= 2;
  }
  while (n % 3 === 0) {
    n /= 3;
  }
  while (n % 5 === 0) {
    n /= 5;
  }
  return n === 1;
};

/**
 * Find next 5-smooth number >= n
 */
const getNextSmooth = (n: number): number => {
  while (!isSmooth(n)) {
    n++;
  }
  return n;
};

/**
 * Recursive FFT using Float64Array for performance
 * Handles any 5-smooth length efficiently
 */
const fftRecursive = (
  real: Float64Array,
  imag: Float64Array
): { real: Float64Array; imag: Float64Array } => {
  const N = real.length;

  if (N <= 1) {
    {
      return { real, imag };
    }
  }

  // For N=2, do butterfly directly
  if (N === 2) {
    const r0 = real[0],
      r1 = real[1];
    const i0 = imag[0],
      i1 = imag[1];
    return {
      real: new Float64Array([r0 + r1, r0 - r1]),
      imag: new Float64Array([i0 + i1, i0 - i1]),
    };
  }

  // Find smallest prime factor (guaranteed to be 2, 3, or 5 for smooth numbers)
  let factor = 2;
  if (N % 2 !== 0) {
    if (N % 3 === 0) {
      {
        factor = 3;
      }
    } else if (N % 5 === 0) {
      factor = 5;
    } else {
      // Should not happen if N is smooth, but fallback for safety
      let f = 5;
      while (f * f <= N) {
        if (N % f === 0) {
          factor = f;
          break;
        }
        f += 2;
      }
      if (factor === 2 && N % 2 !== 0) {
        factor = N;
      }
    }
  }

  const m = N / factor;

  // Decimation
  // Create 'factor' arrays of size 'm'
  const subReal = new Array(factor);
  const subImag = new Array(factor);

  for (let p = 0; p < factor; p++) {
    subReal[p] = new Float64Array(m);
    subImag[p] = new Float64Array(m);
    for (let i = 0; i < m; i++) {
      subReal[p][i] = real[i * factor + p];
      subImag[p][i] = imag[i * factor + p];
    }
  }

  // Recursive FFTs
  const subResults = new Array(factor);
  for (let p = 0; p < factor; p++) {
    subResults[p] = fftRecursive(subReal[p], subImag[p]);
  }

  // Twiddle and combine
  const outReal = new Float64Array(N);
  const outImag = new Float64Array(N);

  for (let k = 0; k < N; k++) {
    let sumReal = 0;
    let sumImag = 0;

    for (let p = 0; p < factor; p++) {
      const angle = (-2 * Math.PI * k * p) / N;
      const twiddleReal = Math.cos(angle);
      const twiddleImag = Math.sin(angle);

      const r = subResults[p].real[k % m];
      const i = subResults[p].imag[k % m];

      // Complex multiply: (r + ji) * (tr + jti)
      // = (r*tr - i*ti) + j(r*ti + i*tr)
      const termReal = r * twiddleReal - i * twiddleImag;
      const termImag = r * twiddleImag + i * twiddleReal;

      sumReal += termReal;
      sumImag += termImag;
    }
    outReal[k] = sumReal;
    outImag[k] = sumImag;
  }

  return { real: outReal, imag: outImag };
};

/**
 * Inverse FFT
 */
const ifft = (
  real: Float64Array,
  imag: Float64Array
): { real: Float64Array; imag: Float64Array } => {
  const N = real.length;
  // Conjugate input: (a + bi)* = a - bi
  const conjImag = new Float64Array(N);
  for (let i = 0; i < N; i++) {
    conjImag[i] = -imag[i];
  }

  // Forward FFT
  const result = fftRecursive(real, conjImag);

  // Conjugate result and scale by 1/N
  const outReal = new Float64Array(N);
  const outImag = new Float64Array(N);
  const scale = 1.0 / N;

  for (let i = 0; i < N; i++) {
    outReal[i] = result.real[i] * scale;
    outImag[i] = -result.imag[i] * scale; // Conjugate part
  }

  return { real: outReal, imag: outImag };
};

/**
 * FFT frequencies
 */
const fftfreq = (n: number, d: number = 1.0): Float64Array => {
  const val = 1.0 / (n * d);
  const results = new Float64Array(n);
  const N = Math.floor((n - 1) / 2) + 1;
  for (let i = 0; i < N; i++) {
    results[i] = i * val;
  }
  for (let i = N; i < n; i++) {
    results[i] = -(n - i) * val;
  }
  return results;
};

/**
 * Smooth using FFT with Low-Pass Parabolic Filter
 */
export const smooth_fft = (
  data: number[],
  windowSize: number = 50,
  delta_t: number = 1
): number[] => {
  if (data.length === 0) {
    return [];
  }

  const n = data.length;

  // Baseline
  const n1 = Math.max(1, Math.floor(0.01 * n));
  const mean_start = mean(data.slice(0, n1));
  const mean_end = mean(data.slice(-n1));
  const baseline = linspace(mean_start, mean_end, n);

  // Detrend
  // Pad to next 5-smooth number for efficiency
  const n_smooth = getNextSmooth(n);

  const real = new Float64Array(n_smooth);
  const imag = new Float64Array(n_smooth); // Init to 0

  for (let i = 0; i < n; i++) {
    real[i] = data[i] - baseline[i];
  }
  // Padding area remains 0, which is correct for detrended data (approx)

  // FFT
  const fftResult = fftRecursive(real, imag);

  // Filter
  const cutoff_frequency = 1 / (2 * windowSize);
  // Frequencies must be calculated for the PADDED length
  const frequencies = fftfreq(n_smooth, delta_t);

  for (let i = 0; i < n_smooth; i++) {
    const freq = frequencies[i];
    const filter_val =
      Math.abs(freq) <= cutoff_frequency
        ? 1 - Math.pow(freq / cutoff_frequency, 2)
        : 0;

    fftResult.real[i] *= filter_val;
    fftResult.imag[i] *= filter_val;
  }

  // IFFT
  const smoothedResult = ifft(fftResult.real, fftResult.imag);

  // Add baseline back and truncate to original length
  const output = new Array(n);
  for (let i = 0; i < n; i++) {
    output[i] = smoothedResult.real[i] + baseline[i];
  }

  return output;
};
